export type EnvironmentKey = 'dev' | 'qa' | 'stage' | 'prod';

export interface EnvironmentInfo {
  key: EnvironmentKey;
  displayName: string;
  suffix: string;
  badgeColor: string;
  badgeTextColor: string;
  isProduction: boolean;
}

const APP_NAME = 'HorsePower Lite';
const APP_SHORT_NAME = 'HPLite';

const ENVIRONMENT_ALIASES: Record<string, EnvironmentKey> = {
  dev: 'dev',
  development: 'dev',
  qa: 'qa',
  stage: 'stage',
  stg: 'stage',
  prod: 'prod',
  production: 'prod',
};

const ENVIRONMENT_DISPLAY: Record<EnvironmentKey, string> = {
  dev: 'DEV',
  qa: 'QA',
  stage: 'STG',
  prod: 'PROD',
};

const ENVIRONMENT_COLORS: Record<EnvironmentKey, { badge: string; text: string }> = {
  dev: { badge: '#e67e22', text: '#ffffff' },
  qa: { badge: '#7596FF', text: '#ffffff' },
  stage: { badge: '#f1c40f', text: '#2b2b2b' },
  prod: { badge: '#838584', text: '#ffffff' },
};

const normalizeMode = (mode: string | undefined) => (mode ?? '').trim().toLowerCase();

export const getEnvironmentInfo = (mode?: string): EnvironmentInfo => {
  const resolvedMode =
    normalizeMode(mode) || normalizeMode((import.meta as { env?: { MODE?: string } }).env?.MODE);
  const key = ENVIRONMENT_ALIASES[resolvedMode] ?? 'dev';
  const displayName = ENVIRONMENT_DISPLAY[key];
  const isProduction = key === 'prod';
  const suffix = isProduction ? '' : ` - ${displayName}`;
  const colors = ENVIRONMENT_COLORS[key];

  return {
    key,
    displayName,
    suffix,
    badgeColor: colors.badge,
    badgeTextColor: colors.text,
    isProduction,
  };
};

export const formatAppTitle = (mode?: string): string => {
  const environment = getEnvironmentInfo(mode);
  return `${APP_NAME}${environment.suffix}`;
};

export const formatAppShortName = (mode?: string): string => {
  const environment = getEnvironmentInfo(mode);
  if (environment.isProduction) {
    return APP_SHORT_NAME;
  }
  return `${APP_SHORT_NAME} - ${environment.displayName}`;
};

export const formatPageTitle = (pageTitle: string, mode?: string): string => {
  const title = pageTitle.trim();
  if (!title) {
    return formatAppTitle(mode);
  }
  return `${title} - ${formatAppTitle(mode)}`;
};
